/**
 * กฎของรหัสผ่าน ใช้ร่วมกันทั้ง client และ server
 *
 * client ใช้เพื่อบอกผู้ใช้ทันทีว่ารหัสที่พิมพ์ยังไม่ผ่านเกณฑ์อะไร
 * server ใช้เป็นตัวตัดสินจริง ไม่เชื่อผลจาก client
 *
 * แนวทางที่ยึด: ความยาวสำคัญกว่าความซับซ้อน
 * การบังคับให้มีอักขระพิเศษทำให้คนตั้งรหัสแบบ Password1! ซึ่งเดาง่ายกว่ารหัสยาว ๆ
 * ที่จำได้ จึงบังคับแค่ความยาวขั้นต่ำและกันรหัสที่แย่ชัด ๆ
 */

export interface PasswordPolicy {
  minLength: number;
}

export interface PasswordCheck {
  valid: boolean;
  /** เหตุผลภาษาไทยที่แสดงให้ผู้ใช้เห็นได้ทันที */
  problems: string[];
}

/**
 * รหัสที่พบบ่อยจนเดาได้ในไม่กี่ครั้ง
 * ไม่ได้ครอบคลุมทุกกรณี แต่กันรหัสที่แย่ที่สุดซึ่งคนมักตั้งตอนถูกบังคับให้เปลี่ยน
 */
const COMMON_PASSWORDS = [
  'password',
  'passw0rd',
  '12345678',
  '123456789',
  '1234567890',
  'qwertyuiop',
  'iloveyou',
  'admin1234',
  'administrator',
  'meterpoint',
  'abcd1234',
  'aaaaaaaaaa',
  'thailand',
  'bangkok123',
];

/** ตรวจว่าเป็นอักขระซ้ำ ๆ หรือเรียงติดกัน เช่น aaaaaaaaaa หรือ 1234567890 */
function isLowEntropy(value: string): boolean {
  const lower = value.toLowerCase();
  if (new Set(lower).size <= 3) return true;

  let ascending = 1;
  let descending = 1;
  for (let i = 1; i < lower.length; i++) {
    const diff = lower.charCodeAt(i) - lower.charCodeAt(i - 1);
    ascending = diff === 1 ? ascending + 1 : 1;
    descending = diff === -1 ? descending + 1 : 1;
    if (ascending >= 6 || descending >= 6) return true;
  }
  return false;
}

export function checkPassword(
  password: string,
  policy: PasswordPolicy,
  context: { email?: string; displayName?: string } = {},
): PasswordCheck {
  const problems: string[] = [];
  const value = String(password ?? '');

  if (value.length < policy.minLength) {
    problems.push(`ต้องยาวอย่างน้อย ${policy.minLength} ตัวอักษร`);
  }
  if (value.length > 200) {
    problems.push('ยาวเกินไป ต้องไม่เกิน 200 ตัวอักษร');
  }
  if (value !== value.trim()) {
    problems.push('ต้องไม่มีช่องว่างที่หัวหรือท้าย');
  }

  const lower = value.toLowerCase();
  if (COMMON_PASSWORDS.some((common) => lower === common || lower.includes(common))) {
    problems.push('รหัสนี้ถูกเดาได้ง่ายเกินไป กรุณาตั้งใหม่');
  }
  if (value.length >= policy.minLength && isLowEntropy(value)) {
    problems.push('อย่าใช้ตัวอักษรซ้ำกันหรือเรียงติดกัน เช่น 1234567890');
  }

  // ห้ามใช้อีเมลหรือชื่อตัวเองเป็นรหัส เพราะเป็นสิ่งแรกที่คนเดา
  const localPart = context.email?.split('@')[0]?.toLowerCase();
  if (localPart && localPart.length >= 4 && lower.includes(localPart)) {
    problems.push('ต้องไม่มีชื่อผู้ใช้หรืออีเมลของตัวเองอยู่ในรหัส');
  }
  if (context.displayName) {
    const name = context.displayName.trim().toLowerCase();
    if (name.length >= 4 && lower.includes(name)) {
      problems.push('ต้องไม่มีชื่อของตัวเองอยู่ในรหัส');
    }
  }

  return { valid: problems.length === 0, problems };
}

/**
 * สร้างรหัสชั่วคราวให้ผู้ดูแลระบบส่งต่อให้ผู้ใช้ทางโทรศัพท์หรือ LINE
 *
 * ตัดอักขระที่อ่านผิดกันได้ออก (0/O, 1/l/I) เพราะรหัสนี้ถูกบอกด้วยปากเป็นหลัก
 * ใส่ขีดคั่นเป็นกลุ่มให้อ่านออกเสียงและจดง่าย เช่น K7RM-4TQP-9WVX
 */
export function generateTemporaryPassword(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);

  let out = '';
  for (let i = 0; i < 12; i++) {
    if (i > 0 && i % 4 === 0) out += '-';
    out += alphabet[bytes[i]! % alphabet.length];
  }
  return out;
}
